// Normalizes two different error dialects into one shape the UI reads:
//   { code, title, message, retryable, details }
//
// Dialect 1 (designed contract, docs/backend-requirements.html):
//   { error: { code, message, details } } with semantic HTTP status
//   (422 outside_coverage, 501 not_implemented, 503 upstream_unavailable).
// Dialect 2 (live GET /risk catalog service):
//   { code: "ERROR", message } with (400 bad input incl. CONUS coverage,
//   404 unknown risk, 502 upstream transport failure).

const COPY = {
    outside_coverage: {
        title: 'Pin is outside the PRISM grid',
        retryable: false,
    },
    no_data: {
        // Indicator-neutral on purpose: freeze reads daily minima, arid reads an
        // index, chill_hours reads an hourly total, so naming any one of them here
        // would be wrong for the others. The per-indicator wording lives in
        // riskPresentation.js#noDataTitle.
        title: 'No records returned for this period',
        retryable: false,
    },
    not_implemented: {
        title: "This indicator isn't computed yet",
        retryable: false,
    },
    upstream_unavailable: {
        title: 'Data source is temporarily unavailable',
        retryable: true,
    },
    invalid_request: {
        title: 'Request could not be completed',
        retryable: false,
    },
    // The real code the live /risk/probability uses for generic bad-input
    // (e.g. an unknown `indicator`) — distinct from invalid_request only in
    // name, not in how the UI treats it.
    invalid_parameter: {
        title: 'Request could not be completed',
        retryable: false,
    },
    timeout: {
        title: 'The request took too long',
        retryable: true,
    },
    network: {
        title: 'Could not reach the risk service',
        retryable: true,
    },
    unknown: {
        title: 'Something went wrong',
        retryable: true,
    },
};

function build(code, message, details) {
    const copy = COPY[code] || COPY.unknown;
    return { code, title: copy.title, message: message || copy.title, retryable: copy.retryable, details };
}

// Pulls "(50.0, 8.0)" out of the live API's CONUS-coverage message so the
// outside_coverage card can echo coordinates even against that dialect.
function coordsFromMessage(message) {
    const m = /\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)/.exec(message || '');
    if (!m) return null;
    return { lat: Number(m[1]), lon: Number(m[2]) };
}

export function normalizeRiskError(err, params) {
    if (err && (err.code === 'ERR_CANCELED' || (err.message && /cancel/i.test(err.message)))) {
        // A canceled request is not an error — the caller must not commit it.
        return null;
    }
    if (!err || !err.response) {
        if (err && err.code === 'ECONNABORTED') return build('timeout');
        return build('network');
    }

    const { status, data } = err.response;
    const designed = data && data.error;
    const live = data && data.code === 'ERROR';

    // Designed contract dialect ({ error: { code, message, details } }) is
    // authoritative regardless of status code — the real backend uses 422
    // for outside_coverage, 400 for generic invalid_parameter, and 501/503
    // for not_implemented/upstream_unavailable, all in this same envelope.
    // An unrecognized code still gets a sensible bucket from the status.
    if (designed) {
        const code = COPY[designed.code] ? designed.code : (status >= 500 ? 'upstream_unavailable' : 'invalid_request');
        return build(code, designed.message, designed.details);
    }
    if (status === 502 && live) {
        return build('upstream_unavailable', data.message);
    }
    if (status === 400 && live) {
        if (/PRISM|CONUS/i.test(data.message || '')) {
            return build('outside_coverage', data.message, coordsFromMessage(data.message) || {
                lat: params && params.lat,
                lon: params && params.lon,
            });
        }
        return build('invalid_request', data.message);
    }
    if (status === 404) {
        return build('invalid_request', data && data.message);
    }
    return build('unknown', (data && data.message) || err.message);
}
